/**
 * A session's kind as v2 shows it (TMMAPP-0153 items 2 and 4; visual contract v2 §11): the list's
 * capitals tag and the chat header's privacy line, each in its tone with its icon.
 *
 * - **Shared** (peach: `warningText`, the people icon): "Shared with {names}" / "{names} and your
 *   assistant can see this conversation.", the names being the served `members[]`.
 * - **Breakout** (sage: `successText`, the branch icon): "Breakout · private" / "Only you can see
 *   this. It uses context from the shared session."
 * - **Private** (sage, the lock icon): "Private", or "Private · {virtual account}" for a sharer's own
 *   session on a virtual account / "Only you can see this conversation."
 * - **Help & feedback** (the help icon): "Help & feedback" / "Feedback goes to the support team."
 *   The prototype's lavender ink isn't a role, so its tag and line are `text2` (as a lavender privacy
 *   chip's text is `text`).
 * Names go through `personName` and `formatList`; a name that isn't served is left out.
 */
import type { TFunction } from 'i18next';
import type { AssistantSession } from '@/api/assistant';
import type { MeVirtualAccount } from '@/api/me';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import type { AppTokens } from '@/theme';

export type SessionKind = 'shared' | 'breakout' | 'private' | 'support';

export function sessionKindOf(session: AssistantSession): SessionKind {
  if (session.kind === 'SUPPORT') return 'support';
  if (session.visibility === 'SHARED') return 'shared';
  if (session.visibility === 'BREAKOUT') return 'breakout';
  return 'private';
}

/** The tone's text colour on `bg` and `surface` (both guaranteed at 4.5:1, hovered too). */
export function sessionKindInk(kind: SessionKind, tokens: AppTokens): string {
  if (kind === 'shared') return tokens.warningText;
  if (kind === 'support') return tokens.text2;
  return tokens.successText;
}

/** The members other than the person, by name; the unnamed are left out. */
export function otherMemberNames(t: TFunction, session: AssistantSession): string[] {
  return session.members.flatMap((member) =>
    member.mine || (member.name?.trim() ?? '') === '' ? [] : [personName(member.name, t)],
  );
}

/** Every member by name, the person included (the shared privacy line names them all). */
function memberNames(t: TFunction, session: AssistantSession): string[] {
  return session.members.flatMap((member) =>
    (member.name?.trim() ?? '') === '' ? [] : [personName(member.name, t)],
  );
}

/** The virtual account a session reads, by name: its scope's, else the person's list; null if neither. */
export function sessionVirtualAccountName(
  session: AssistantSession,
  virtualAccounts: readonly Pick<MeVirtualAccount, 'id' | 'name'>[],
): string | null {
  const id = session.virtualAccountId;
  if (id === null) return null;
  const scoped = session.scope.find(
    (item) => item.kind === 'VIRTUAL_ACCOUNT' && item.id === id && item.name !== null,
  );
  return scoped?.name ?? virtualAccounts.find((entry) => entry.id === id)?.name ?? null;
}

/** The list's capitals tag (styled in capitals; the catalog holds sentence case). */
export function sessionTagLabel(
  t: TFunction,
  session: AssistantSession,
  locale: string,
  virtualAccountName: string | null,
): string {
  const kind = sessionKindOf(session);
  if (kind === 'support') return t('assistant.tag.support');
  if (kind === 'breakout') return t('assistant.tag.breakout');
  if (kind === 'shared') {
    const names = otherMemberNames(t, session);
    return names.length > 0
      ? t('assistant.tag.shared', { names: formatList(names, locale) })
      : t('assistant.tag.sharedUnnamed');
  }
  return virtualAccountName !== null
    ? t('assistant.tag.privateOn', { virtualAccount: virtualAccountName })
    : t('assistant.tag.private');
}

/** The chat header's privacy line. */
export function privacyLineCopy(t: TFunction, session: AssistantSession, locale: string): string {
  const kind = sessionKindOf(session);
  if (kind === 'support') return t('assistant.privacyLine.support');
  if (kind === 'breakout') return t('assistant.privacyLine.breakout');
  if (kind === 'private') return t('assistant.privacyLine.private');
  const names = memberNames(t, session);
  return names.length > 0
    ? t('assistant.privacyLine.shared', {
        people: formatList([...names, t('assistant.privacyLine.yourAssistant')], locale),
      })
    : t('assistant.privacyLine.sharedUnnamed');
}
